// Workspace painter — assigns each tile a varied gradient via [data-e16-tile=N]
// regardless of its DOM position. Targets:
//   • Frappe-default-blue inline-styled icons (workspace home tiles)
//   • Sidebar items in MAIN section only (NOT left rail — those stay small)
//   • .widget.shortcut-widget-box (replace innerHTML with tile structure)
//
// Workspace tiles are painted in-place; no global page decoration is added.

import { svg, iconForLabel } from "../utils/icons.js";
import { hashStr } from "../utils/hash.js";

let overrides = {};

function tileIndex(label) {
	if (!label) return 7; // slate fallback
	if (overrides[label]) {
		const map = { emerald: 1, rose: 2, amber: 3, sky: 4, walnut: 5, fuchsia: 6, slate: 7,
			coral: 8, mint: 9, sun: 10, smoke: 7, iris: 11, ocean: 12, cocoa: 13, pink: 14 };
		return map[overrides[label]] || 7;
	}
	return (hashStr(label) % 14) + 1;
}

function findLabelFor(el) {
	if (el.getAttribute("title")) return el.getAttribute("title");
	if (el.parentElement?.getAttribute("title")) return el.parentElement.getAttribute("title");

	const c = el.closest("[item-name],[data-name],[data-page-name],[data-workspace]");
	if (c) {
		const v = c.getAttribute("item-name") ||
			c.getAttribute("data-name") ||
			c.getAttribute("data-page-name") ||
			c.getAttribute("data-workspace") || "";
		if (v) return v;
	}

	const container = el.closest(".sidebar-item-container, .standard-sidebar-item, .widget, .module-card, .item-anchor, a, [class*='card'], [class*='tile']");
	if (container) {
		const labelEl = container.querySelector(
			".sidebar-item-label, .module-name, .widget-title .ellipsis, .widget-title, .ellipsis"
		);
		if (labelEl) {
			const txt = labelEl.textContent.trim();
			if (txt && txt.length < 80) return txt;
		}
	}

	const a = el.closest("a");
	if (a) {
		const txt = a.textContent.trim();
		if (txt && txt.length < 80) return txt;
	}

	return "";
}

function isInMainSection(el) {
	return !!el.closest(".layout-main-section, .layout-main-section-wrapper, .desk-page, .editor-js-container");
}

function isInSidebar(el) {
	return !!el.closest(".body-sidebar, .desk-sidebar");
}

// === Pass 1: Find tiles with Frappe's default blue inline style ===
// Strategy: paint the OUTERMOST tile container (not nested children). If a
// candidate has another candidate as ancestor in the same tile group, we
// skip the inner one. This avoids the double-circle artifact.
function paintBlueTiles() {
	const candidates = Array.from(document.querySelectorAll(
		'[style*="background-color: rgb(74, 163, 223)"], ' +
		'[style*="background-color:rgb(74, 163, 223)"], ' +
		'[style*="background-color: #4aa3df"], ' +
		'[style*="background-color:#4aa3df"], ' +
		'[style*="background-color: #4AA3DF"]'
	));

	// Filter to in-main only, drop sidebar items (their bg gets nuked)
	const main = candidates.filter((el) => {
		if (isInSidebar(el)) {
			el.style.setProperty("background-color", "transparent", "important");
			return false;
		}
		return isInMainSection(el);
	});

	// For each candidate, find the highest ancestor that is the visible tile.
	// We walk up to find the tile boundary: stop at .sidebar-item-container,
	// .widget, .ce-block, or a flex/grid item — whichever is closest.
	main.forEach((el) => {
		const tileBoundary = el.closest(
			".sidebar-item-container, .standard-sidebar-item, .widget, .ce-block, [class*='card']"
		) || el.parentElement;
		if (!tileBoundary) return;

		// Pick the largest visible icon-like wrapper inside this boundary —
		// usually .sidebar-item-icon or .app-icon's parent
		const outerCandidates = [
			tileBoundary.querySelector(".sidebar-item-icon"),
			tileBoundary.querySelector(".module-icon"),
			tileBoundary.querySelector(".workspace-icon"),
			tileBoundary.querySelector(".app-icon"),
		].filter(Boolean);

		// Use the OUTERMOST element (the one that contains all the others)
		let outer = outerCandidates[0] || el;
		for (const c of outerCandidates) {
			if (c.contains(outer) && c !== outer) outer = c;
		}

		const label = findLabelFor(outer);
		if (!label) return;

		const idx = tileIndex(label);
		// Mark the OUTER one for our gradient
		if (!outer.dataset.e16Tile) outer.setAttribute("data-e16-tile", String(idx));

		// Strip background-color from EVERY blue-styled descendant inside outer
		outer.querySelectorAll('[style*="rgb(74, 163, 223)"], [style*="#4aa3df"], [style*="#4AA3DF"]').forEach((inner) => {
			if (inner !== outer) {
				inner.style.setProperty("background", "transparent", "important");
				inner.style.setProperty("background-color", "transparent", "important");
				inner.style.setProperty("box-shadow", "none", "important");
			}
		});

		// Replace inner contents with a single clean Lucide SVG if none visible
		const hasIcon = outer.querySelector("svg.lucide, [data-e16-icon-injected]");
		if (!hasIcon) {
			const iconName = iconForLabel(label);
			// Find the deepest icon container to replace its content
			const innerHost =
				outer.querySelector(".app-icon") ||
				outer.querySelector(".app-icon-svg") ||
				outer;
			const wasOuter = innerHost === outer;
			const svgMarkup = svg(iconName, { size: 28 });
			if (wasOuter) {
				outer.innerHTML = `<span data-e16-icon-injected style="display:grid;place-items:center;color:#fff;">${svgMarkup}</span>`;
			} else {
				innerHost.innerHTML = `<span data-e16-icon-injected style="display:grid;place-items:center;color:#fff;">${svgMarkup}</span>`;
				innerHost.style.setProperty("background", "transparent", "important");
				innerHost.style.setProperty("background-color", "transparent", "important");
			}
		}
	});

	// Also catch generic .app-icon / .module-icon (no inline blue) in main
	document.querySelectorAll(".app-icon, .module-icon, [class*='workspace-icon']").forEach((el) => {
		if (isInSidebar(el)) return;
		if (!isInMainSection(el)) return;
		if (el.dataset.e16Tile) return;
		// Skip skeleton placeholders
		if (el.classList.contains("placeholder") || el.closest(".placeholder")) return;
		// Skip if an ancestor is already painted
		if (el.parentElement?.closest("[data-e16-tile]")) return;

		const label = findLabelFor(el);
		if (!label) return;
		const idx = tileIndex(label);
		el.setAttribute("data-e16-tile", String(idx));

		const hasIcon = el.querySelector("svg, use, i.fa");
		if (!hasIcon) {
			const iconName = iconForLabel(label);
			el.innerHTML = `<span data-e16-icon-injected style="display:grid;place-items:center;color:#fff;">${svg(iconName, { size: 28 })}</span>`;
		}
	});
}

// === Pass 2: shortcut widget DOM replacement (text rows → tile cards) ===
function paintShortcutWidget(widget) {
	if (widget.dataset.e16Painted) return;
	// Skip skeleton/placeholder elements — Frappe renders these while the
	// workspace content is still loading. Painting them replaces the skeleton
	// HTML before Frappe can replace them with real content, causing blanks.
	if (widget.classList.contains("placeholder")) return;
	if (widget.closest(".placeholder")) return;
	const labelEl = widget.querySelector(".widget-title .ellipsis, .widget-title");
	const label = (labelEl && labelEl.textContent.trim()) || widget.getAttribute("data-widget-name") || "";
	if (!label) return;

	if (!widget.dataset.e16OrigHtml) widget.dataset.e16OrigHtml = widget.innerHTML;

	const idx = tileIndex(label);
	const iconName = iconForLabel(label);

	widget.innerHTML = `
		<div class="e16-card-tile" data-e16-tile="${idx}">
			<div class="e16-card-tile__icon">${svg(iconName, { size: 28 })}</div>
		</div>
		<div class="e16-card-tile__label">${label}</div>
	`;
	widget.classList.add("e16-card-painted");
	widget.dataset.e16Painted = label;
}

function unpaintAll() {
	document.querySelectorAll('[data-e16-tile]').forEach((el) => {
		el.removeAttribute("data-e16-tile");
	});
	document.querySelectorAll(".e16-card-painted").forEach((widget) => {
		if (widget.dataset.e16OrigHtml) {
			widget.innerHTML = widget.dataset.e16OrigHtml;
			delete widget.dataset.e16OrigHtml;
		}
		widget.classList.remove("e16-card-painted");
		delete widget.dataset.e16Painted;
	});
}

function paintAll() {
	const html = document.documentElement;
	const shapes = document.getElementById("e16-floating-shapes-root");
	if (shapes) shapes.remove();

	if (!html.classList.contains("e16-paint-workspace")) {
		unpaintAll();
		return;
	}
	paintBlueTiles();
	document.querySelectorAll(".widget.shortcut-widget-box").forEach(paintShortcutWidget);
}

function loadOverrides() {
	// Use fetch() instead of frappe.db.get_doc() to avoid triggering the
	// Frappe request.js bug where statusCode[200] is called from .fail()
	// with only one argument, making the second xhr param undefined and
	// throwing "Cannot read properties of undefined (reading 'responseText')".
	const params = new URLSearchParams({
		doctype: "ERPNext16 Theme Settings",
		name: "ERPNext16 Theme Settings",
	});
	return fetch(`/api/method/frappe.client.get?${params}`, {
		credentials: "same-origin",
		headers: { Accept: "application/json" },
	})
		.then((r) => (r.ok ? r.json() : null))
		.then((r) => {
			overrides = {};
			try {
				(r?.message?.tile_overrides || []).forEach((row) => {
					if (row.label && row.color_id) overrides[row.label] = row.color_id;
				});
			} catch (e) {}
		})
		.catch(() => {
			overrides = {};
		});
}

export const workspacePainter = {
	mount() {
		// Suppress Frappe's uncaught jqXHR rejection objects — these occur when
		// jQuery AJAX calls fail and the rejection reason is the raw XHR object.
		// This is a known Frappe request.js bug (statusCode[200] called with wrong
		// arg count from .fail()). Suppress to keep console clean and prevent
		// unhandled rejection noise from masking real errors.
		if (!window.__e16_xhr_guard) {
			window.__e16_xhr_guard = true;
			window.addEventListener("unhandledrejection", (e) => {
				if (
					e.reason &&
					typeof e.reason === "object" &&
					"readyState" in e.reason &&
					typeof e.reason.getResponseHeader === "function"
				) {
					e.preventDefault();
				}
			});
		}

		loadOverrides().then(() => paintAll()).catch(() => {});
		// Start at 600ms — 150ms was too aggressive and fired before Frappe
		// finishes rendering workspace widgets from its own async API call.
		[600, 1200, 2400, 4800].forEach((d) => setTimeout(paintAll, d));

		document.addEventListener("e16:paint-applied", paintAll);

		try {
			if (window.frappe && frappe.router) {
				frappe.router.on("change", () => {
					setTimeout(paintAll, 350);
					setTimeout(paintAll, 1200);
				});
			}
		} catch (e) {}
	},
};
